import React, { useEffect, useMemo, useState } from "react";
import "./AdminPortal.css";
import { forgotPassword, getSession, hasPermission, login, logout, resetPassword } from "./adminApi";
import AdminAccountModule from "./AdminAccountModule";
import NewsletterModule from "./newsletter/NewsletterModule";
import AdminUsersModule from "./AdminUsersModule";

const MODULES = [
  {
    key: "newsletter",
    title: "Newsletter",
    description: "Manage subscribers, campaigns and newsletter history.",
    icon: "✉",
    permission: "newsletter.view",
  },
  {
    key: "quiz",
    title: "Quiz",
    description: "Create quizzes, manage questions and review results.",
    icon: "✓",
    permission: "quiz.view",
  },
  {
    key: "students",
    title: "Students",
    description: "View registrations, records and student reports.",
    icon: "◉",
    permission: "students.view",
  },
  {
    key: "reports",
    title: "Reports",
    description: "Access operational reports and analytics.",
    icon: "▥",
    permission: "reports.view",
  },
  {
    key: "admins",
    title: "Admin Users",
    description: "Manage administrators, roles and access.",
    icon: "♙",
    permission: "admin_users.view",
  },
  {
    key: "audit",
    title: "Audit Log",
    description: "Review administrative activity and security events.",
    icon: "◌",
    permission: "audit.view",
  },
];

function initials(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A";
}

export default function AdminPortal() {
  const [view, setView] = useState("loading");
  const [user, setUser] = useState(null);
  const [csrfToken, setCsrfToken] = useState("");
  const [activeModule, setActiveModule] = useState("dashboard");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotNotice, setForgotNotice] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resetConfirmValue, setResetConfirmValue] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetNotice, setResetNotice] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const hash = window.location.hash || "";
    if (hash.startsWith("#reset-password")) {
      const query = hash.includes("?") ? hash.split("?")[1] : "";
      const params = new URLSearchParams(query);
      const token = params.get("token") || "";
      if (token) {
        setResetToken(token);
        setView("reset");
        return () => { mounted = false; };
      }
    }

    getSession()
      .then((data) => {
        if (!mounted) return;
        setUser(data.user);
        setCsrfToken(data.csrf_token || "");
        setView("authenticated");
      })
      .catch(() => {
        if (!mounted) return;
        setView("login");
      });

    return () => {
      mounted = false;
    };
  }, []);

  const visibleModules = useMemo(
    () => MODULES.filter((module) => hasPermission(user, module.permission)),
    [user]
  );

  async function handleLogin(event) {
    event.preventDefault();
    setLoginError("");
    setLoggingIn(true);

    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") || "").trim();
    const password = String(form.get("password") || "");

    try {
      const data = await login(email, password);
      setUser(data.user);
      setCsrfToken(data.csrf_token || "");
      setActiveModule("dashboard");
      setView("authenticated");
    } catch (error) {
      setLoginError(error.message || "Unable to sign in.");
    } finally {
      setLoggingIn(false);
    }
  }

  async function handleForgotPassword(event) {
    event.preventDefault();
    setForgotLoading(true);
    setForgotNotice("");
    try {
      const data = await forgotPassword(forgotEmail.trim());
      setForgotNotice(data.message || "If an active administrator account exists for that email address, a password reset link has been sent.");
    } catch (error) {
      setForgotNotice(error.message || "Unable to process the request.");
    } finally {
      setForgotLoading(false);
    }
  }

  async function handleResetPassword(event) {
    event.preventDefault();
    setResetLoading(true);
    setResetError("");
    setResetNotice("");
    try {
      const data = await resetPassword(resetToken, resetPasswordValue, resetConfirmValue);
      setResetNotice(data.message || "Password reset successful.");
      setResetPasswordValue("");
      setResetConfirmValue("");
      window.setTimeout(() => {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        window.location.hash = "";
        setResetToken("");
        setResetNotice("");
        setView("login");
      }, 1200);
    } catch (error) {
      setResetError(error.message || "Unable to reset password.");
    } finally {
      setResetLoading(false);
    }
  }

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout(csrfToken);
    } catch {
      // Even if the server session has already expired, return to login.
    } finally {
      setUser(null);
      setCsrfToken("");
      setActiveModule("dashboard");
      setView("login");
      setLoggingOut(false);
    }
  }

  if (view === "loading") {
    return (
      <div className="bb-admin-loading">
        <div className="bb-admin-spinner" />
        <span>Checking secure session…</span>
      </div>
    );
  }

  if (view === "reset") {
    return (
      <div className="bb-admin-shell bb-admin-login-shell">
        <div className="bb-admin-login-grid">
          <section className="bb-admin-brand-panel">
            <div className="bb-admin-brand-mark">B</div>
            <p className="bb-admin-eyebrow">BHOOMI BIOTECHNOLOGY</p>
            <h1>Secure password recovery</h1>
            <p className="bb-admin-brand-copy">
              Create a new administrator password using your time-limited recovery link.
            </p>
            <div className="bb-admin-security-note">
              <span>●</span>
              <div><strong>One-time recovery link</strong><small>The link expires after 30 minutes.</small></div>
            </div>
          </section>
          <section className="bb-admin-login-panel">
            <div className="bb-admin-login-card">
              <div className="bb-admin-mobile-mark">B</div>
              <p className="bb-admin-eyebrow">RESET PASSWORD</p>
              <h2>Set a new password</h2>
              <p className="bb-admin-muted">Choose a new password with at least 12 characters.</p>
              <form onSubmit={handleResetPassword} className="bb-admin-form">
                <label><span>New password</span><input type="password" autoComplete="new-password" value={resetPasswordValue} onChange={(event) => setResetPasswordValue(event.target.value)} minLength={12} required disabled={resetLoading} /></label>
                <label><span>Confirm new password</span><input type="password" autoComplete="new-password" value={resetConfirmValue} onChange={(event) => setResetConfirmValue(event.target.value)} minLength={12} required disabled={resetLoading} /></label>
                {resetError ? <div className="bb-admin-error" role="alert">{resetError}</div> : null}
                {resetNotice ? <div className="bb-admin-success" role="status">{resetNotice}</div> : null}
                <button className="bb-admin-primary-btn" type="submit" disabled={resetLoading}>{resetLoading ? "Updating…" : "Set new password"}</button>
              </form>
              <p className="bb-admin-login-footer">Bhoomi BioTechnology · Administration</p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (view === "forgot") {
    return (
      <div className="bb-admin-shell bb-admin-login-shell">
        <div className="bb-admin-login-grid">
          <section className="bb-admin-brand-panel">
            <div className="bb-admin-brand-mark">B</div>
            <p className="bb-admin-eyebrow">BHOOMI BIOTECHNOLOGY</p>
            <h1>Account recovery</h1>
            <p className="bb-admin-brand-copy">Request a secure, time-limited password reset link for your administrator account.</p>
            <div className="bb-admin-security-note"><span>●</span><div><strong>Protected recovery</strong><small>We never reveal whether an email is registered.</small></div></div>
          </section>
          <section className="bb-admin-login-panel">
            <div className="bb-admin-login-card">
              <div className="bb-admin-mobile-mark">B</div>
              <p className="bb-admin-eyebrow">FORGOT PASSWORD</p>
              <h2>Recover access</h2>
              <p className="bb-admin-muted">Enter your administrator email address.</p>
              <form onSubmit={handleForgotPassword} className="bb-admin-form">
                <label><span>Email address</span><input type="email" value={forgotEmail} onChange={(event) => setForgotEmail(event.target.value)} autoComplete="username" required disabled={forgotLoading} /></label>
                {forgotNotice ? <div className="bb-admin-success" role="status">{forgotNotice}</div> : null}
                <button className="bb-admin-primary-btn" type="submit" disabled={forgotLoading}>{forgotLoading ? "Sending…" : "Send reset link"}</button>
                <button className="bb-admin-text-btn" type="button" onClick={() => { setForgotNotice(""); setView("login"); }} disabled={forgotLoading}>Back to sign in</button>
              </form>
              <p className="bb-admin-login-footer">Bhoomi BioTechnology · Administration</p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (view === "login") {
    return (
      <div className="bb-admin-shell bb-admin-login-shell">
        <div className="bb-admin-login-grid">
          <section className="bb-admin-brand-panel">
            <div className="bb-admin-brand-mark">B</div>
            <p className="bb-admin-eyebrow">BHOOMI BIOTECHNOLOGY</p>
            <h1>Administration Portal</h1>
            <p className="bb-admin-brand-copy">
              A secure workspace for managing newsletters, quizzes, student
              registrations, reports and administrative access.
            </p>
            <div className="bb-admin-security-note">
              <span>●</span>
              <div>
                <strong>Protected access</strong>
                <small>Session-based authentication with role permissions</small>
              </div>
            </div>
          </section>

          <section className="bb-admin-login-panel">
            <div className="bb-admin-login-card">
              <div className="bb-admin-mobile-mark">B</div>
              <p className="bb-admin-eyebrow">ADMIN SIGN IN</p>
              <h2>Welcome back</h2>
              <p className="bb-admin-muted">
                Sign in with your administrator account to continue.
              </p>

              <form onSubmit={handleLogin} className="bb-admin-form">
                <label>
                  <span>Email address</span>
                  <input
                    name="email"
                    type="email"
                    autoComplete="username"
                    placeholder="admin@example.com"
                    required
                    disabled={loggingIn}
                  />
                </label>

                <label>
                  <span>Password</span>
                  <input
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    required
                    disabled={loggingIn}
                  />
                </label>

                {loginError && (
                  <div className="bb-admin-error" role="alert">
                    {loginError}
                  </div>
                )}

                <button className="bb-admin-primary-btn" type="submit" disabled={loggingIn}>
                  {loggingIn ? "Signing in…" : "Sign in"}
                </button>
              </form>

              <button className="bb-admin-text-btn bb-admin-forgot-link" type="button" onClick={() => { setForgotEmail(""); setForgotNotice(""); setView("forgot"); }}>Forgot password?</button>

              <p className="bb-admin-login-footer">
                Bhoomi BioTechnology · Administration
              </p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="bb-admin-app">
      <aside className="bb-admin-sidebar">
        <div className="bb-admin-sidebar-brand">
          <div className="bb-admin-brand-mark small">B</div>
          <div>
            <strong>Bhoomi BioTech</strong>
            <span>Admin Portal</span>
          </div>
        </div>

        <nav className="bb-admin-nav" aria-label="Administration">
          <button
            className={`bb-admin-nav-item ${activeModule === "dashboard" ? "active" : ""}`}
            onClick={() => setActiveModule("dashboard")}
          >
            <span>⌂</span>
            Dashboard
          </button>

          {visibleModules.map((module) => (
            <button
              key={module.key}
              className={`bb-admin-nav-item ${
                activeModule === module.key ? "active" : ""
              }`}
              onClick={() => setActiveModule(module.key)}
            >
              <span>{module.icon}</span>
              {module.title}
            </button>
          ))}

          <button
            className={`bb-admin-nav-item ${activeModule === "account" ? "active" : ""}`}
            onClick={() => setActiveModule("account")}
          >
            <span>◌</span>
            My Account
          </button>
        </nav>

        <div className="bb-admin-sidebar-bottom">
          <div className="bb-admin-user-mini">
            <div className="bb-admin-avatar">{initials(user?.name)}</div>
            <div>
              <strong>{user?.name}</strong>
              <span>{user?.roles?.join(", ") || "Administrator"}</span>
            </div>
          </div>

          <button
            className="bb-admin-logout"
            onClick={handleLogout}
            disabled={loggingOut}
          >
            {loggingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </aside>

      <main className="bb-admin-main">
        <header className="bb-admin-topbar">
          <div>
            <p className="bb-admin-eyebrow">CONTROL CENTER</p>
            <h1>{activeModule === "dashboard" ? "Dashboard" : activeModule === "account" ? "My Account" : MODULES.find((m) => m.key === activeModule)?.title}</h1>
          </div>
          <div className="bb-admin-top-user">
            <div className="bb-admin-avatar">{initials(user?.name)}</div>
            <div>
              <strong>{user?.name}</strong>
              <span>{user?.email}</span>
            </div>
          </div>
        </header>

        {activeModule === "dashboard" ? (
          <section className="bb-admin-content">
            <div className="bb-admin-welcome">
              <div>
                <span className="bb-admin-chip">SECURE SESSION</span>
                <h2>Hello, {user?.name?.split(" ")[0] || "Admin"}.</h2>
                <p>
                  Your administration workspace is ready. Access is controlled
                  by your assigned roles and permissions.
                </p>
              </div>
              <div className="bb-admin-welcome-symbol">B</div>
            </div>

            <div className="bb-admin-section-heading">
              <div>
                <h3>Available modules</h3>
                <p>Only modules permitted for your account are shown.</p>
              </div>
            </div>

            <div className="bb-admin-module-grid">
              {visibleModules.length === 0 ? (
                <div className="bb-admin-empty">
                  No modules are currently assigned to this account.
                </div>
              ) : (
                visibleModules.map((module) => (
                  <button
                    key={module.key}
                    className="bb-admin-module-card"
                    onClick={() => setActiveModule(module.key)}
                  >
                    <span className="bb-admin-module-icon">{module.icon}</span>
                    <span className="bb-admin-module-title">{module.title}</span>
                    <span className="bb-admin-module-description">
                      {module.description}
                    </span>
                    <span className="bb-admin-module-link">Open module →</span>
                  </button>
                ))
              )}
            </div>

            <div className="bb-admin-info-grid">
              <div className="bb-admin-info-card">
                <span className="bb-admin-info-label">ROLE</span>
                <strong>{user?.roles?.join(", ") || "Administrator"}</strong>
              </div>
              <div className="bb-admin-info-card">
                <span className="bb-admin-info-label">PERMISSIONS</span>
                <strong>{user?.permissions?.length || 0}</strong>
              </div>
              <div className="bb-admin-info-card">
                <span className="bb-admin-info-label">ACCOUNT STATUS</span>
                <strong className="status-active">{user?.status || "active"}</strong>
              </div>
            </div>
          </section>
        ) : activeModule === "newsletter" ? (
          <section className="bb-admin-content">
            <NewsletterModule
              user={user}
              csrfToken={csrfToken}
            />
          </section>
        ) : activeModule === "admins" ? (
          <section className="bb-admin-content">
            <AdminUsersModule
              user={user}
              csrfToken={csrfToken}
            />
          </section>
        ) : activeModule === "account" ? (
          <section className="bb-admin-content">
            <AdminAccountModule
              user={user}
              csrfToken={csrfToken}
              onSessionUpdate={(data) => {
                if (data?.csrf_token) setCsrfToken(data.csrf_token);
              }}
            />
          </section>
        ) : (
          <section className="bb-admin-content">
            <div className="bb-admin-placeholder">
              <div className="bb-admin-placeholder-icon">
                {MODULES.find((m) => m.key === activeModule)?.icon || "•"}
              </div>
              <span className="bb-admin-chip">PHASE 1 FOUNDATION</span>
              <h2>{MODULES.find((m) => m.key === activeModule)?.title}</h2>
              <p>
                Authentication and role-based access are ready. This module
                will be implemented in the next development phase.
              </p>
              <button
                className="bb-admin-secondary-btn"
                onClick={() => setActiveModule("dashboard")}
              >
                Back to dashboard
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
